import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import Dropdown from 'react-bootstrap/Dropdown';
import BlazeToggle from 'components/ui/toggle/BlazeToggle';
import styles from 'views/MockServices/Tabs/MockServices/MockServicesListBlazeTable.module.css';
import _ from 'lodash';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import BzmIcon from 'components/icons/BzmIcon';

interface AssignTemplateModalProps {
  show: boolean;
  onCancel: () => void;
  onAssignTemplate: (template: MockServiceTemplate) => void;
  templates: MockServiceTemplate[];
  assignedTemplateId?: number;
}

const AssignTemplateModal = (props: AssignTemplateModalProps) => {
  const { t } = useTranslation('mockServices');

  /**
   * Builds template state indicating selected template. If there is any template assigned to the virtual service
   * (id is not null), we use this template as default one.
   */
  useEffect(() => {
    if (!_.isNil(props.assignedTemplateId)) {
      selectTemplate(props.assignedTemplateId);
    } else {
      setTemplate({} as MockServiceTemplate);
    }
  }, [props.templates]);

  const [template, setTemplate] = useState({} as MockServiceTemplate);
  const [isApplyInProgress, setIsApplyInProgress] = useState(false);

  const selectTemplate = (templateId: number) => {
    const template = props.templates.find(
      (t: MockServiceTemplate) => t.id === templateId || t.serviceMockTemplateId === templateId,
    );
    if (template) {
      setTemplate(template);
    }
  };

  const dropdownItems = props.templates.map((t: MockServiceTemplate) => (
    <Dropdown.Item
      id={`qa-dropdown-item-template-${t.id}`}
      onClick={() => selectTemplate(t.id)}
      key={t.id}
      title={t.name}
    >
      {t.name}
    </Dropdown.Item>
  ));

  const assignTemplate = async () => {
    await setIsApplyInProgress(true);
    await props.onAssignTemplate(template);
    await setIsApplyInProgress(false);
    resetTemplateWithDelay();
  };

  // Reset selection after fadeOut animation is finished
  const resetTemplateWithDelay = () => {
    setTimeout(() => {
      setTemplate({} as MockServiceTemplate);
    }, 500);
  };

  const onCancel = async () => {
    await props.onCancel();
    resetTemplateWithDelay();
  };

  return (
    <Modal id="qa-modal-assign-template" show={props.show} centered={true} backdrop={true} onHide={onCancel}>
      <Modal.Header closeButton id="qa-modal-assign-template-btn-close-icon">
        <h5 className="modal-title">{t('assignTemplateModal.applyTemplate')}</h5>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-warning" role="alert">
          <BzmIcon variant={'icon-bzm-caution'} className={'alert-icon'} />
          <p className="alert-message mb-0">{t('assignTemplateModal.applyTemplateInfo')}</p>
        </div>
        <label htmlFor="qa-modal-assign-template-toggle">{t('assignTemplateModal.templateLabel')}</label>
        <Dropdown>
          <Dropdown.Toggle id="qa-modal-assign-template-toggle" as={BlazeToggle} disabled={_.isEmpty(props.templates)}>
            {template.name
              ? template.name
              : _.isEmpty(props.templates)
              ? t('assignTemplateModal.noTemplates')
              : t('assignTemplateModal.selectTemplate')}
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.endpointPicker}>
            <div className="dropdown-menu-limited">{dropdownItems}</div>
          </Dropdown.Menu>
        </Dropdown>
      </Modal.Body>
      <Modal.Footer>
        <Button id={'qa-modal-assign-template-btn-cancel'} variant="link" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          id={'qa-modal-assign-template-btn-apply'}
          variant="primary"
          disabled={_.isEmpty(template) || isApplyInProgress}
          onClick={() => assignTemplate()}
        >
          {t('common:actions.apply')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignTemplateModal;
